(() => {
  'use strict';

  const NS = 'eti-community';
  const STORAGE = {
    likes: `${NS}:likes:v1`,
    build: `${NS}:garage:v2`,
    legacyBuild: `${NS}:advisor-build:v1`,
    submission: `${NS}:submission-draft:v1`,
    discussions: `${NS}:discussion-state:v2`
  };
  const focusableSelector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const parseJSON = (value, fallback) => {
    try { return JSON.parse(value); } catch (_error) { return fallback; }
  };
  const readStore = (key, fallback) => {
    try { return parseJSON(localStorage.getItem(key), fallback); } catch (_error) { return fallback; }
  };
  const removeStore = (key) => { try { localStorage.removeItem(key); } catch (_error) { /* Storage may be unavailable. */ } };
  const writeStore = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_error) { /* Storage may be unavailable. */ }
  };
  const text = (value) => String(value == null ? '' : value);
  const normalized = (value) => text(value).trim().toLowerCase();
  const list = (value) => Array.isArray(value) ? value : text(value).split(/[,|]/).map((item) => item.trim()).filter(Boolean);
  const motionOK = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const q = (root, selector) => root.querySelector(selector);
  const qa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const announce = (root, message) => {
    const region = q(root, '[data-eti-community-live]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 20);
  };

  function dispatch(root, name, detail = {}) {
    root.dispatchEvent(new CustomEvent(`${NS}:${name}`, { bubbles: true, detail }));
  }

  function getDialog(root, type) {
    return q(root, `[data-eti-dialog="${type}"]`);
  }

  function openDialog(dialog, opener) {
    if (!dialog) return;
    dialog.__etiCommunityOpener = opener || document.activeElement;
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('eti-community-dialog-open');
    const focusTarget = q(dialog, '[autofocus]') || q(dialog, focusableSelector);
    window.requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
    dispatch(dialog.closest('[data-eti-community-root]') || dialog, 'dialog-open', { type: dialog.dataset.etiDialog });
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    if (!document.querySelector('[data-eti-dialog][open]')) document.documentElement.classList.remove('eti-community-dialog-open');
    const opener = dialog.__etiCommunityOpener;
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    dispatch(dialog.closest('[data-eti-community-root]') || dialog, 'dialog-close', { type: dialog.dataset.etiDialog });
  }

  function trapDialogFocus(event) {
    const dialog = event.currentTarget;
    if (event.key !== 'Tab') return;
    const items = qa(dialog, focusableSelector).filter((node) => node.offsetParent !== null);
    if (!items.length) { event.preventDefault(); dialog.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function setupDialogs(root) {
    qa(root, '[data-eti-dialog]').forEach((dialog) => {
      dialog.addEventListener('keydown', trapDialogFocus);
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog && dialog.dataset.etiDialog !== 'submission') closeDialog(dialog);
      });
      dialog.addEventListener('close', () => {
        if (!document.querySelector('[data-eti-dialog][open]')) document.documentElement.classList.remove('eti-community-dialog-open');
      });
    });
  }

  function buildPayload(card) {
    const embedded = q(card, 'script[data-eti-build-data]');
    const data = embedded ? parseJSON(embedded.textContent, {}) : {};
    const gallery = data.gallery || parseJSON(card.dataset.etiGallery || '[]', []);
    return {
      id: data.id || card.dataset.etiBuildId || card.id || '',
      title: data.title || card.dataset.etiBuildTitle || q(card, '[data-eti-build-title]')?.textContent || '',
      owner: data.owner || card.dataset.etiBuildOwner || q(card, '[data-eti-build-owner]')?.textContent || '',
      country: data.country || card.dataset.etiBuildCountry || '',
      vehicle: data.vehicle || card.dataset.etiBuildVehicle || '',
      story: data.story || card.dataset.etiBuildStory || '',
      tags: data.tags || list(card.dataset.etiBuildTags),
      gallery: Array.isArray(gallery) ? gallery : [],
      products: Array.isArray(data.products) ? data.products : []
    };
  }

  function renderGallery(root, dialog, gallery, title) {
    const image = q(dialog, '[data-eti-gallery-image]');
    const caption = q(dialog, '[data-eti-gallery-caption]');
    const status = q(dialog, '[data-eti-gallery-status]');
    const prev = q(dialog, '[data-eti-action="gallery-prev"]');
    const next = q(dialog, '[data-eti-action="gallery-next"]');
    dialog.__etiCommunityGallery = { items: gallery, index: 0, title };
    const update = () => {
      const state = dialog.__etiCommunityGallery;
      const item = state.items[state.index];
      if (!image || !item) {
        image?.setAttribute('hidden', '');
        if (caption) caption.textContent = '';
        if (status) status.textContent = 'No gallery images configured.';
        prev?.setAttribute('disabled', '');
        next?.setAttribute('disabled', '');
        return;
      }
      image.removeAttribute('hidden');
      image.src = typeof item === 'string' ? item : item.src || '';
      image.alt = typeof item === 'string' ? `${title} gallery image` : item.alt || `${title} gallery image ${state.index + 1}`;
      if (caption) caption.textContent = typeof item === 'string' ? '' : item.caption || '';
      if (status) status.textContent = `${state.index + 1} of ${state.items.length}`;
      const disabled = state.items.length < 2;
      prev?.toggleAttribute('disabled', disabled);
      next?.toggleAttribute('disabled', disabled);
      dispatch(root, 'gallery-change', { index: state.index, count: state.items.length });
    };
    dialog.__etiCommunityGallery.update = update;
    update();
  }

  function moveGallery(dialog, direction) {
    const state = dialog?.__etiCommunityGallery;
    if (!state?.items?.length) return;
    state.index = (state.index + direction + state.items.length) % state.items.length;
    state.update();
  }

  function setupDomGallery(root, dialog) {
    const slides = qa(dialog, '[data-eti-gallery-slide]');
    const status = q(dialog, '[data-eti-gallery-status]');
    const prev = q(dialog, '[data-eti-action="gallery-prev"]');
    const next = q(dialog, '[data-eti-action="gallery-next"]');
    dialog.__etiCommunityGallery = {
      items: slides,
      index: 0,
      update() {
        slides.forEach((slide, index) => slide.toggleAttribute('hidden', index !== this.index));
        if (status) status.textContent = `${this.index + 1} / ${Math.max(1, slides.length)}`;
        prev?.toggleAttribute('disabled', slides.length < 2);
        next?.toggleAttribute('disabled', slides.length < 2);
        dispatch(root, 'gallery-change', { index: this.index, count: slides.length });
      }
    };
    dialog.__etiCommunityGallery.update();
    setupSwipeForStage(q(dialog, '[data-eti-gallery]'), dialog);
  }

  function populateBuildDialog(root, card, opener) {
    const dialog = getDialog(root, 'build');
    if (!dialog) return;
    const template = q(root, `template[data-eti-build-template="${CSS.escape(card.dataset.etiBuildId || '')}"]`);
    const content = q(dialog, '[data-eti-dialog-content]');
    if (template && content) {
      content.replaceChildren(template.content.cloneNode(true));
      setupDomGallery(root, dialog);
      openDialog(dialog, opener);
      return;
    }
    const data = buildPayload(card);
    const set = (selector, value) => { const node = q(dialog, selector); if (node) node.textContent = text(value); };
    set('[data-eti-build-dialog-title]', data.title);
    set('[data-eti-build-dialog-owner]', data.owner);
    set('[data-eti-build-dialog-vehicle]', data.vehicle);
    set('[data-eti-build-dialog-country]', data.country);
    set('[data-eti-build-dialog-story]', data.story);
    const tags = q(dialog, '[data-eti-build-dialog-tags]');
    if (tags) {
      tags.replaceChildren(...data.tags.map((tag) => {
        const span = document.createElement('span'); span.textContent = tag; return span;
      }));
    }
    const products = q(dialog, '[data-eti-build-dialog-products]');
    if (products) {
      products.replaceChildren(...data.products.map((product) => {
        const link = document.createElement('a');
        link.href = product.url || '#';
        link.textContent = product.title || 'View product';
        if (!product.url) link.setAttribute('aria-disabled', 'true');
        return link;
      }));
      products.closest('[data-eti-build-products-wrap]')?.toggleAttribute('hidden', !data.products.length);
    }
    dialog.dataset.etiBuildId = data.id;
    renderGallery(root, dialog, data.gallery, data.title || 'Community build');
    openDialog(dialog, opener);
  }

  function setupBuilds(root) {
    const cards = () => qa(root, '[data-eti-build-card]');
    qa(root, '[data-eti-build-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        const filter = normalized(button.dataset.etiBuildFilter || 'all');
        qa(root, '[data-eti-build-filter]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        let visible = 0;
        cards().forEach((card) => {
          const tags = list(card.dataset.etiBuildTags || card.dataset.etiTags).map(normalized);
          const show = filter === 'all' || tags.includes(filter);
          card.toggleAttribute('hidden', !show);
          if (show) visible += 1;
        });
        q(root, '[data-eti-build-empty]')?.toggleAttribute('hidden', visible > 0);
        announce(root, `${visible} build${visible === 1 ? '' : 's'} shown.`);
        dispatch(root, 'filter-change', { filter, visible });
      });
    });
    const initialFilter = q(root, '[data-eti-build-filter].is-active');
    if (initialFilter && normalized(initialFilter.dataset.etiBuildFilter) !== 'all') initialFilter.click();
    const likes = readStore(STORAGE.likes, {}) || {};
    cards().forEach((card) => {
      const id = card.dataset.etiBuildId;
      const button = q(card, '[data-eti-action="like-build"]');
      if (button && id && likes[id]) {
        button.classList.add('is-liked'); button.setAttribute('aria-pressed', 'true');
      }
    });
  }

  function readAdvisorProducts(root) {
    const script = q(root, 'script[data-eti-advisor-data]');
    const parsed = script ? parseJSON(script.textContent, []) : [];
    return Array.isArray(parsed) ? parsed : Array.isArray(parsed.products) ? parsed.products : [];
  }

  function readGarageVehicles(root) {
    const script = q(root, 'script[data-eti-vehicle-data]');
    const parsed = script ? parseJSON(script.textContent, {}) : {};
    return Array.isArray(parsed) ? parsed : Array.isArray(parsed.vehicles) ? parsed.vehicles : [];
  }

  function emptyGarage() {
    return { version: 2, vehicle: {}, components: [] };
  }

  function readGarage() {
    const saved = readStore(STORAGE.build, null);
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      return { version: 2, vehicle: saved.vehicle || {}, components: Array.isArray(saved.components) ? saved.components : [] };
    }
    const legacy = readStore(STORAGE.legacyBuild, []);
    const garage = emptyGarage();
    if (Array.isArray(legacy)) {
      garage.components = legacy.map((id) => ({ id: `eti:${id}`, productId: String(id), title: 'ETi component', category: 'ETi product', status: 'Planned', source: 'recommendation' }));
    }
    return garage;
  }

  function saveGarage(root) {
    writeStore(STORAGE.build, root.__etiCommunityGarage || emptyGarage());
  }

  const productComponentId = (id) => `eti:${String(id || '')}`;

  function componentFromProduct(product, source = 'recommendation') {
    const productId = String(product.id || product.handle || '');
    return {
      id: productComponentId(productId), productId, title: product.title || 'ETi component',
      brand: 'ETi', category: product.rules?.priority || product.rules?.stage || 'ETi product',
      status: source === 'purchase' ? 'Purchased' : 'Planned', source,
      url: product.url || '', image: product.image || '', purchaseLabel: product.purchaseLabel || ''
    };
  }

  function formValues(form) {
    const values = {};
    new FormData(form).forEach((value, key) => {
      if (value instanceof File) return;
      if (Object.prototype.hasOwnProperty.call(values, key)) values[key] = [].concat(values[key], value);
      else values[key] = value;
    });
    return values;
  }

  function advisorMatch(product, values) {
    const rules = product.rules || {};
    const fields = ['make', 'model', 'chassis', 'goal', 'priority'];
    const configuredRank = Number(product.rank || 0);
    let score = Number.isFinite(configuredRank) ? configuredRank : 0;
    let constrained = 0;
    fields.forEach((field) => {
      const allowed = list(rules[field] || product[field]).map(normalized);
      if (!allowed.length || allowed.includes('all') || allowed.includes('*')) return;
      constrained += 1;
      const selected = list(values[field] || values[field === 'model' ? 'model_chassis' : field]).map(normalized);
      if (selected.some((value) => allowed.some((rule) => value === rule || value.includes(rule)))) score += 10;
      else score -= 100;
    });
    return { product, score, constrained, eligible: score > -50 };
  }

  function productCard(product, inGarage) {
    const article = document.createElement('article');
    article.className = 'eti-community__advisor-product';
    article.dataset.etiProductId = product.id || product.handle || '';
    if (product.image) {
      const img = document.createElement('img'); img.src = product.image; img.alt = product.imageAlt || product.title || ''; img.loading = 'lazy'; article.append(img);
    }
    const body = document.createElement('div');
    const heading = document.createElement('h4'); heading.textContent = product.title || 'Configured product'; body.append(heading);
    if (product.price) { const price = document.createElement('p'); price.textContent = product.price; body.append(price); }
    if (product.available === false) { const status = document.createElement('p'); status.textContent = 'Currently unavailable'; body.append(status); }
    const actions = document.createElement('div'); actions.className = 'eti-community__advisor-product-actions';
    if (product.url) { const link = document.createElement('a'); link.href = product.url; link.textContent = 'VIEW PRODUCT'; actions.append(link); }
    const add = document.createElement('button'); add.type = 'button'; add.dataset.etiAction = 'add-to-build'; add.dataset.etiProductId = product.id || product.handle || ''; add.textContent = inGarage ? 'IN GARAGE' : 'ADD TO GARAGE'; add.disabled = inGarage; actions.append(add);
    body.append(actions); article.append(body); return article;
  }

  function setupAdvisor(root) {
    const form = q(root, '[data-eti-advisor-form]');
    if (!form) return;
    const products = readAdvisorProducts(root);
    const vehicles = readGarageVehicles(root);
    root.__etiCommunityProducts = products;
    root.__etiCommunityGarage = readGarage();
    root.__etiCommunityGarage.components = root.__etiCommunityGarage.components.map((component) => {
      const product = products.find((item) => String(item.id || item.handle) === String(component.productId));
      return product ? { ...componentFromProduct(product, component.source), ...component } : component;
    });
    const summary = q(root, '[data-eti-advisor-summary]');
    const results = q(root, '[data-eti-advisor-results]');
    const empty = q(root, '[data-eti-advisor-empty]');
    const buildWrap = q(root, '[data-eti-advisor-build-list], [data-eti-build-list]');
    const buildList = buildWrap?.matches('ul') ? buildWrap : q(buildWrap, 'ul');
    const garageEmpty = q(root, '[data-eti-garage-empty]');
    const garageCount = q(root, '[data-eti-garage-count]');
    const makeSelect = q(form, '[data-eti-vehicle-make]');
    const modelSelect = q(form, '[data-eti-vehicle-model]');
    const chassisInput = q(form, '[data-eti-vehicle-chassis]');
    const customMakeWrap = q(form, '[data-eti-custom-make]');
    const customVehicleWrap = q(form, '[data-eti-custom-vehicle]');

    const makes = Array.from(new Set(vehicles.map((vehicle) => text(vehicle.make).trim()).filter(Boolean))).sort();
    if (makeSelect) {
      makes.forEach((make) => { const option = document.createElement('option'); option.value = make; option.textContent = make; makeSelect.append(option); });
      const custom = document.createElement('option'); custom.value = '__custom'; custom.textContent = 'Other / custom'; makeSelect.append(custom);
    }

    const populateModels = (make, selectedId = '') => {
      if (!modelSelect) return;
      modelSelect.replaceChildren();
      const prompt = document.createElement('option'); prompt.value = ''; prompt.textContent = make ? 'Select a vehicle' : 'Select a make first'; modelSelect.append(prompt);
      const matched = vehicles.filter((vehicle) => vehicle.make === make);
      matched.forEach((vehicle) => {
        const option = document.createElement('option'); option.value = vehicle.id;
        option.textContent = [vehicle.model, vehicle.chassis].filter(Boolean).join(' / ');
        modelSelect.append(option);
      });
      if (make === '__custom') { const option = document.createElement('option'); option.value = '__custom'; option.textContent = 'Enter a custom vehicle'; modelSelect.append(option); modelSelect.value = '__custom'; }
      else if (selectedId) modelSelect.value = selectedId;
      modelSelect.disabled = !make;
    };

    const savedVehicle = root.__etiCommunityGarage.vehicle || {};
    if (savedVehicle.make && makeSelect) {
      const knownMake = makes.includes(savedVehicle.make);
      makeSelect.value = knownMake ? savedVehicle.make : '__custom';
      populateModels(makeSelect.value, savedVehicle.id || '');
      if (!knownMake) {
        const customMake = q(form, '[name="custom_make"]'); if (customMake) customMake.value = savedVehicle.make || '';
        const customChassis = q(form, '[name="custom_chassis"]'); if (customChassis) customChassis.value = savedVehicle.chassis || savedVehicle.model || '';
      }
    } else populateModels('');
    ['year', 'engine', 'goal', 'budget'].forEach((name) => { const control = q(form, `[name="${name}"]`); if (control && savedVehicle[name]) control.value = savedVehicle[name]; });
    list(savedVehicle.priority).forEach((priority) => { const input = q(form, `[name="priority"][value="${CSS.escape(priority)}"]`); if (input) input.checked = true; });

    const advisorValues = () => {
      const values = formValues(form);
      const configured = vehicles.find((vehicle) => String(vehicle.id) === String(values.vehicle));
      const custom = values.make === '__custom';
      values.make = custom ? values.custom_make : values.make;
      values.model = configured?.model || (custom ? values.custom_chassis : '');
      values.chassis = configured?.chassis || (custom ? values.custom_chassis : '');
      if (chassisInput) chassisInput.value = values.chassis || '';
      return { values, configured };
    };

    const updateBuildList = () => {
      if (!buildList) return;
      const components = root.__etiCommunityGarage.components || [];
      buildList.replaceChildren(...components.map((component) => {
        const li = document.createElement('li');
        const copy = document.createElement('div');
        const meta = document.createElement('p'); meta.textContent = [component.category, component.status].filter(Boolean).join(' / ');
        const title = document.createElement('strong'); title.textContent = [component.brand, component.title].filter(Boolean).join(' — ') || 'Component';
        copy.append(meta, title);
        if (component.notes) { const notes = document.createElement('small'); notes.textContent = component.notes; copy.append(notes); }
        const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.etiAction = 'remove-from-build'; remove.dataset.etiComponentId = component.id; remove.setAttribute('aria-label', `Remove ${component.title || 'component'} from garage`); remove.textContent = 'Remove';
        li.append(copy, remove); return li;
      }));
      garageEmpty?.toggleAttribute('hidden', components.length > 0);
      if (garageCount) garageCount.textContent = `${components.length} ${components.length === 1 ? 'item' : 'items'}`;
    };

    root.__etiCommunityAddComponent = (component) => {
      if (!component?.id) return false;
      const exists = root.__etiCommunityGarage.components.some((item) => String(item.id) === String(component.id));
      if (exists) return false;
      root.__etiCommunityGarage.components.push(component); saveGarage(root); update(); return true;
    };

    const update = () => {
      const { values, configured } = advisorValues();
      const custom = makeSelect?.value === '__custom';
      customMakeWrap?.toggleAttribute('hidden', !custom);
      customVehicleWrap?.toggleAttribute('hidden', !custom);
      if (configured && !q(form, '[name="engine"]')?.value && configured.engine) q(form, '[name="engine"]').placeholder = configured.engine;
      root.__etiCommunityGarage.vehicle = { id: configured?.id || '', make: values.make || '', model: values.model || '', chassis: values.chassis || '', year: values.year || '', engine: values.engine || '', goal: values.goal || '', budget: values.budget || '', priority: list(values.priority) };
      saveGarage(root);
      const titleParts = [values.year, values.make, values.model].filter(Boolean);
      if (summary) {
        const heading = document.createElement('h3'); heading.textContent = titleParts.length ? titleParts.join(' ') : 'Choose your vehicle';
        const detail = document.createElement('p'); detail.textContent = [values.chassis, values.engine, values.goal].filter(Boolean).join(' / ') || 'Your standardized build record starts here.';
        summary.replaceChildren(heading, detail);
      }
      const ranked = products.map((product) => advisorMatch(product, values)).filter((match) => match.eligible).sort((a, b) => b.score - a.score);
      if (results) {
        const groups = new Map();
        ranked.forEach(({ product }) => {
          const stage = product.rules?.stage || product.stage || 'Configured recommendations';
          if (!groups.has(stage)) groups.set(stage, []);
          groups.get(stage).push(product);
        });
        const fragments = Array.from(groups, ([stage, stageProducts]) => {
          const group = document.createElement('section');
          group.className = 'eti-community__advisor-stage';
          const heading = document.createElement('h4');
          heading.textContent = stage;
          const grid = document.createElement('div');
          grid.className = 'eti-community__product-grid';
          grid.append(...stageProducts.map((product) => productCard(product, root.__etiCommunityGarage.components.some((component) => component.id === productComponentId(product.id || product.handle)))));
          group.append(heading, grid);
          return group;
        });
        results.replaceChildren(...fragments);
      }
      qa(root, '[data-eti-advisor-product]').forEach((card) => {
        const id = card.dataset.etiAdvisorProduct;
        const product = products.find((item) => String(item.blockId) === String(id));
        const match = product && advisorMatch(product, values);
        card.toggleAttribute('hidden', !match?.eligible);
        const add = q(card, '[data-eti-action="add-to-build"]');
        const productId = add?.dataset.productId || product?.id;
        if (add && productId) { add.disabled = root.__etiCommunityGarage.components.some((component) => component.id === productComponentId(productId)); add.textContent = add.disabled ? 'In garage' : 'Add to garage'; }
      });
      empty?.toggleAttribute('hidden', ranked.length > 0);
      dispatch(root, 'advisor-change', { values, recommendations: ranked.length });
      updateBuildList();
    };
    makeSelect?.addEventListener('change', () => { populateModels(makeSelect.value); update(); });
    form.addEventListener('input', update);
    form.addEventListener('change', update);

    const customModForm = q(root, '[data-eti-custom-mod-form]');
    customModForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!customModForm.reportValidity()) return;
      const values = formValues(customModForm);
      const component = { id: `custom:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`, title: text(values.title).trim(), brand: text(values.brand).trim(), category: values.category, status: values.status || 'Installed', notes: text(values.notes).trim(), source: 'custom' };
      if (root.__etiCommunityAddComponent(component)) { customModForm.reset(); announce(root, `${component.title} added to your garage.`); }
    });

    const photoInput = q(root, '[data-eti-garage-photos]');
    const photoGrid = q(root, '[data-eti-garage-photo-grid]');
    photoInput?.addEventListener('change', () => {
      (root.__etiCommunityPhotoUrls || []).forEach((url) => URL.revokeObjectURL(url));
      root.__etiCommunityPhotoUrls = [];
      const figures = Array.from(photoInput.files || []).slice(0, 8).map((file) => {
        const url = URL.createObjectURL(file); root.__etiCommunityPhotoUrls.push(url);
        const figure = document.createElement('figure'); const img = document.createElement('img'); img.src = url; img.alt = file.name; const caption = document.createElement('figcaption'); caption.textContent = file.name; figure.append(img, caption); return figure;
      });
      photoGrid?.replaceChildren(...figures); photoGrid?.toggleAttribute('hidden', !figures.length);
      if (figures.length) announce(root, `${figures.length} photo ${figures.length === 1 ? 'preview' : 'previews'} added for this session.`);
    });
    root.__etiCommunityAdvisorUpdate = update;
    root.__etiCommunityAdvisorBuildUpdate = updateBuildList;
    update();
  }

  function setupTabs(root) {
    const tabs = qa(root, '[role="tab"][data-eti-tab]');
    const activate = (tab, focus = false) => {
      const name = tab.dataset.etiTab;
      tabs.forEach((item) => { const active = item === tab; item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; item.classList.toggle('is-active', active); });
      qa(root, '[role="tabpanel"][data-eti-tab-panel]').forEach((panel) => panel.toggleAttribute('hidden', panel.dataset.etiTabPanel !== name));
      if (focus) tab.focus();
      dispatch(root, 'tab-change', { tab: name });
    };
    root.__etiCommunityActivateTab = activate;
    tabs.forEach((tab) => tab.addEventListener('click', () => activate(tab)));
    tabs.forEach((tab, index) => tab.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      activate(tabs[next], true);
    }));
    const current = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (current) activate(current);
  }

  function photoItems(root) {
    return qa(root, '[data-eti-photo]').map((node) => ({
      node,
      src: node.dataset.etiPhotoSrc || q(node, 'img')?.currentSrc || q(node, 'img')?.src || '',
      alt: node.dataset.etiPhotoAlt || q(node, 'img')?.alt || '',
      caption: node.dataset.etiPhotoCaption || ''
    })).filter((item) => item.src);
  }

  function openLightbox(root, node, opener) {
    const dialog = getDialog(root, 'lightbox');
    if (!dialog) return;
    const id = node.dataset.photoId || node.dataset.etiPhotoId;
    const template = id && q(root, `template[data-eti-photo-template="${CSS.escape(id)}"]`);
    const content = q(dialog, '[data-eti-lightbox-content]');
    if (template && content) {
      const items = qa(root, '[data-eti-photo]').map((photo) => {
        const photoId = photo.dataset.photoId || photo.dataset.etiPhotoId;
        const photoTemplate = photoId && q(root, `template[data-eti-photo-template="${CSS.escape(photoId)}"]`);
        return photoTemplate ? { photo, template: photoTemplate } : null;
      }).filter(Boolean);
      const selectedIndex = Math.max(0, items.findIndex((item) => item.photo === node));
      dialog.__etiCommunityGallery = {
        items,
        index: selectedIndex,
        update() {
          const item = this.items[this.index];
          if (!item) return;
          const controls = document.createElement('div');
          controls.className = 'eti-community__gallery-controls';
          const previous = document.createElement('button'); previous.type = 'button'; previous.dataset.etiAction = 'gallery-prev'; previous.setAttribute('aria-label', 'Previous photo'); previous.textContent = 'Previous';
          const status = document.createElement('span'); status.dataset.etiGalleryStatus = ''; status.setAttribute('aria-live', 'polite'); status.textContent = `${this.index + 1} / ${this.items.length}`;
          const next = document.createElement('button'); next.type = 'button'; next.dataset.etiAction = 'gallery-next'; next.setAttribute('aria-label', 'Next photo'); next.textContent = 'Next';
          previous.disabled = next.disabled = this.items.length < 2;
          controls.append(previous, status, next);
          content.replaceChildren(item.template.content.cloneNode(true), controls);
          setupSwipeForStage(content, dialog);
          dispatch(root, 'gallery-change', { index: this.index, count: this.items.length });
        }
      };
      dialog.__etiCommunityGallery.update();
      openDialog(dialog, opener);
      return;
    }
    const items = photoItems(root);
    const index = Math.max(0, items.findIndex((item) => item.node === node));
    renderGallery(root, dialog, items, 'Community photo');
    dialog.__etiCommunityGallery.index = index;
    dialog.__etiCommunityGallery.update();
    openDialog(dialog, opener);
  }

  async function loadCommunityPhotoFeed(root) {
    const endpoint = root.dataset.etiCommunityFeedUrl;
    const grid = q(root, '[data-eti-installed-photo-feed]');
    const empty = q(root, '[data-eti-installed-photo-empty]');
    if (!endpoint || !grid) return;
    try {
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Community feed returned ${response.status}`);
      const payload = await response.json();
      const photos = Array.isArray(payload.photos) ? payload.photos : [];
      photos.forEach((photo) => {
        if (!photo?.id || !photo?.imageUrl || q(grid, `[data-photo-id="${CSS.escape(String(photo.id))}"]`)) return;
        let imageUrl;
        try {
          imageUrl = new URL(String(photo.imageUrl), endpoint);
          if (!['http:', 'https:'].includes(imageUrl.protocol)) return;
        } catch (_) { return; }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'eti-community__photo';
        button.dataset.etiPhoto = '';
        button.dataset.etiAction = 'photo-open';
        button.dataset.photoId = String(photo.id);
        button.dataset.etiPhotoSrc = imageUrl.href;
        button.dataset.etiPhotoAlt = text(photo.caption || photo.vehicleDetail || 'Customer installed ETi component');
        button.dataset.etiPhotoCaption = text([photo.vehicle, photo.vehicleDetail, photo.caption].filter(Boolean).join(' — '));
        button.setAttribute('aria-label', `Open ${button.dataset.etiPhotoAlt} photo`);
        const image = document.createElement('img');
        image.src = imageUrl.href;
        image.alt = button.dataset.etiPhotoAlt;
        image.loading = 'lazy';
        image.width = 1200;
        image.height = 900;
        const meta = document.createElement('span');
        meta.className = 'eti-community__photo-meta';
        const category = document.createElement('small'); category.textContent = text(photo.category || 'Overall').replace('_', ' ');
        const vehicle = document.createElement('strong'); vehicle.textContent = text(photo.vehicle || photo.vehicleDetail || 'ETi customer build');
        const caption = document.createElement('span'); caption.textContent = text(photo.caption || photo.vehicleDetail || 'Customer installation');
        meta.append(category, vehicle, caption);
        button.append(image, meta);
        grid.append(button);
      });
      const count = qa(grid, '[data-eti-photo]').length;
      grid.toggleAttribute('hidden', count === 0);
      empty?.toggleAttribute('hidden', count > 0);
      if (photos.length) dispatch(root, 'community-feed-loaded', { count: photos.length });
    } catch (error) {
      dispatch(root, 'community-feed-error', { message: error instanceof Error ? error.message : 'Feed unavailable' });
    }
  }

  function setupSwipeForStage(stage, dialog) {
    if (!stage || stage.dataset.etiSwipeReady === 'true') return;
    stage.dataset.etiSwipeReady = 'true';
      let startX = null;
      stage.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') startX = event.clientX; }, { passive: true });
      stage.addEventListener('pointerup', (event) => {
        if (startX == null) return;
        const delta = event.clientX - startX; startX = null;
        if (Math.abs(delta) > 45) moveGallery(dialog || stage.closest('[data-eti-dialog]'), delta < 0 ? 1 : -1);
      }, { passive: true });
  }

  function setupSwipe(root) {
    qa(root, '[data-eti-gallery-stage], [data-eti-gallery]').forEach((stage) => {
      setupSwipeForStage(stage, stage.closest('[data-eti-dialog]'));
    });
  }

  function setupDiscussions(root) {
    const state = readStore(STORAGE.discussions, {}) || {};
    const renderReplies = (card, id) => {
      const target = q(card, `[data-eti-replies="${CSS.escape(id)}"]`);
      const saved = state[id] || {};
      const replies = Array.isArray(saved.replies) ? saved.replies : (saved.savedLocally && saved.draft ? [saved.draft] : []);
      if (target) {
        target.replaceChildren(...replies.map((reply) => {
          const message = document.createElement('p');
          message.textContent = `Local preview comment: ${reply}`;
          return message;
        }));
      }
      const count = q(card, '[data-eti-discussion-comment-count]');
      if (count) count.textContent = String((Number(count.dataset.etiBaseCount) || 0) + replies.length);
    };
    qa(root, '[data-eti-discussion]').forEach((card) => {
      const id = card.dataset.etiDiscussionId;
      if (!id) return;
      renderReplies(card, id);
      if (!state[id]) return;
      const reply = q(card, '[data-eti-reply-field]');
      if (reply && state[id].draft) reply.value = state[id].draft;
      const like = q(card, '[data-eti-action="like-discussion"]');
      if (like && state[id].liked) {
        like.classList.add('is-liked'); like.setAttribute('aria-pressed', 'true');
        const count = q(like, '[data-eti-discussion-like-count]');
        if (count) { if (!count.dataset.etiBaseCount) count.dataset.etiBaseCount = count.textContent.trim(); count.textContent = String((Number(count.dataset.etiBaseCount) || 0) + 1); }
      }
    });
    root.addEventListener('input', (event) => {
      const field = event.target.closest('[data-eti-reply-field]');
      if (!field) return;
      const card = field.closest('[data-eti-discussion]');
      if (!card?.dataset.etiDiscussionId) return;
      state[card.dataset.etiDiscussionId] = { ...(state[card.dataset.etiDiscussionId] || {}), draft: field.value };
      writeStore(STORAGE.discussions, state);
    });
    root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-eti-reply-form]');
      if (!form) return;
      event.preventDefault();
      const card = form.closest('[data-eti-discussion]');
      const field = q(form, '[data-eti-reply-field]');
      const id = card?.dataset.etiDiscussionId;
      if (!id || !field?.value.trim()) { field?.focus(); return; }
      const reply = field.value.trim();
      const replies = Array.isArray(state[id]?.replies) ? state[id].replies : [];
      state[id] = { ...(state[id] || {}), draft: '', savedLocally: true, replies: [...replies, reply] };
      writeStore(STORAGE.discussions, state);
      field.value = '';
      renderReplies(card, id);
      announce(root, 'Comment added to this local preview.');
    });
    root.__etiCommunityDiscussionState = state;
  }

  function setupSubmission(root) {
    const dialog = getDialog(root, 'submission');
    const form = dialog && q(dialog, '[data-eti-submission-form]');
    if (!dialog || !form) return;
    const steps = qa(form, '[data-eti-submission-step]');
    const errorSummary = q(form, '[data-eti-form-errors]');
    const uploadInput = q(form, 'input[name="photos"]');
    const uploadList = q(form, '[data-eti-upload-list]');
    const endpoint = root.dataset.etiCommunitySubmissionUrl || '/apps/eti-community';
    let index = 0;
    let dirty = false;
    let sending = false;
    const saved = readStore(STORAGE.submission, null);
    if (saved?.values) Object.entries(saved.values).forEach(([name, value]) => {
      const fields = qa(form, `[name="${CSS.escape(name)}"]`);
      fields.forEach((field) => {
        if (field.type === 'checkbox' || field.type === 'radio') field.checked = Array.isArray(value) ? value.includes(field.value) : value === field.value;
        else field.value = value;
      });
    });
    const fieldLabel = (field) => field.closest('label')?.querySelector('span')?.textContent?.replace(/Required/gi, '').trim() || field.name || 'Field';
    const setSummary = (message = '') => {
      if (!errorSummary) return;
      errorSummary.textContent = message;
      errorSummary.toggleAttribute('hidden', !message);
    };
    const renderUploads = () => {
      if (!uploadList || !uploadInput) return;
      const files = Array.from(uploadInput.files || []);
      uploadList.replaceChildren(...files.map((file) => {
        const item = document.createElement('div');
        const name = document.createElement('strong');
        const size = document.createElement('span');
        name.textContent = file.name;
        size.textContent = `${(file.size / 1024 / 1024).toFixed(1)} MB`;
        item.append(name, size);
        return item;
      }));
    };
    const validateFiles = () => {
      if (!uploadInput) return;
      const files = Array.from(uploadInput.files || []);
      const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
      const total = files.reduce((sum, file) => sum + file.size, 0);
      let message = '';
      if (!files.length) message = 'Attach at least one build photo.';
      else if (files.length > 8) message = 'Attach no more than 8 photos.';
      else if (files.some((file) => !allowed.has(file.type))) message = 'Use JPG, PNG, or WebP photos.';
      else if (files.some((file) => file.size > 8 * 1024 * 1024)) message = 'Each photo must be 8 MB or smaller.';
      else if (total > 25 * 1024 * 1024) message = 'The combined upload must be 25 MB or smaller.';
      uploadInput.setCustomValidity(message);
    };
    const update = () => {
      steps.forEach((step, stepIndex) => step.toggleAttribute('hidden', stepIndex !== index));
      const explicitProgress = qa(form, '[data-eti-submission-progress]');
      const progressItems = explicitProgress.length ? explicitProgress : qa(form, '[data-eti-progress] > li');
      progressItems.forEach((item, itemIndex) => {
        const current = itemIndex === index;
        item.classList.toggle('is-current', current); item.classList.toggle('is-active', current); item.classList.toggle('is-complete', itemIndex < index);
        if (current) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      });
      const back = q(form, '[data-eti-action="submission-back"]');
      const next = q(form, '[data-eti-action="submission-next"]');
      const submit = q(form, '[data-eti-action="submission-submit"]');
      back?.toggleAttribute('hidden', index === 0);
      next?.toggleAttribute('hidden', index === steps.length - 1);
      submit?.toggleAttribute('hidden', index !== steps.length - 1);
      q(form, '[data-eti-submission-step-status-live]')?.replaceChildren(document.createTextNode(`Step ${index + 1} of ${steps.length}`));
      q(steps[index], 'input, select, textarea, button')?.focus({ preventScroll: true });
      setSummary();
      dispatch(root, 'submission-step', { step: index + 1 });
    };
    const save = () => { writeStore(STORAGE.submission, { version: 1, savedAt: new Date().toISOString(), values: formValues(form) }); };
    const validateStep = () => {
      validateFiles();
      const fields = qa(steps[index], 'input, select, textarea');
      let firstInvalid = null;
      const errors = [];
      for (const field of fields) {
        const valid = field.checkValidity();
        field.toggleAttribute('aria-invalid', !valid);
        const target = q(form, `[data-eti-field-error="${CSS.escape(field.name)}"]`);
        if (target) target.textContent = valid ? '' : field.validationMessage;
        if (!valid) {
          firstInvalid ||= field;
          errors.push(`${fieldLabel(field)}: ${field.validationMessage}`);
        }
      }
      if (errors.length) {
        setSummary(`Please correct ${errors.join(' ')}`);
        firstInvalid?.focus();
        firstInvalid?.reportValidity();
        return false;
      }
      setSummary();
      return true;
    };
    form.addEventListener('input', (event) => {
      dirty = true;
      event.target?.removeAttribute?.('aria-invalid');
      save();
    });
    form.addEventListener('change', () => { dirty = true; validateFiles(); renderUploads(); save(); });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (sending || !validateStep() || !form.checkValidity()) { form.reportValidity(); return; }
      const submit = q(form, '[data-eti-action="submission-submit"]');
      const originalLabel = submit?.textContent || 'Send for ETi review';
      sending = true;
      if (submit) { submit.disabled = true; submit.textContent = 'Uploading securely...'; }
      setSummary();
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body.ok === false) throw new Error(body.error || 'ETi could not receive this submission. Please try again.');
        removeStore(STORAGE.submission);
        dirty = false;
        qa(form, '[data-eti-submission-form-content]').forEach((node) => node.setAttribute('hidden', ''));
        q(form, '[data-eti-submission-success]')?.removeAttribute('hidden');
        announce(root, 'Submission received and queued for ETi review.');
        dispatch(root, 'submission-sent', { submissionId: body.submissionId || '' });
      } catch (error) {
        setSummary(error instanceof Error ? error.message : 'ETi could not receive this submission. Please try again.');
        errorSummary?.scrollIntoView({ behavior: motionOK() ? 'smooth' : 'auto', block: 'center' });
        announce(root, 'Submission was not sent. Review the form message and try again.');
      } finally {
        sending = false;
        if (submit) { submit.disabled = false; submit.textContent = originalLabel.trim(); }
      }
    });
    const requestClose = () => {
      if (!q(form, '[data-eti-submission-success]')?.hasAttribute('hidden')) { closeDialog(dialog); return; }
      if (dirty && !window.confirm('Keep this draft and close the submission?')) return;
      save(); closeDialog(dialog);
    };
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); requestClose(); });
    dialog.__etiCommunityRequestClose = requestClose;
    form.__etiCommunityStep = {
      next() { if (validateStep() && index < steps.length - 1) { index += 1; update(); } },
      back() { if (index > 0) { index -= 1; update(); } },
      reset() {
        form.reset(); removeStore(STORAGE.submission); index = 0; dirty = false; setSummary(); renderUploads();
        qa(form, '[data-eti-submission-form-content]').forEach((node) => node.removeAttribute('hidden'));
        q(form, '[data-eti-submission-success]')?.setAttribute('hidden', ''); update();
      }
    };
    update();
  }

  /**
   * Sends the build assembled on this page into the customer's real ETi Garage.
   *
   * The storefront has no customer-account session token, so this goes through
   * the signed app proxy, which tells the service who is logged in. The local
   * copy is left alone: it is the draft, and the garage is the record.
   */
  async function commitBuild(root, control) {
    const result = q(root, '[data-eti-commit-result]');
    const garage = root.__etiCommunityGarage || readGarage();
    const vehicle = garage.vehicle || {};
    const components = Array.isArray(garage.components) ? garage.components : [];

    const show = (message, tone) => {
      if (!result) return;
      result.textContent = message;
      result.dataset.tone = tone;
      result.hidden = false;
    };

    if (!vehicle.make || !vehicle.model) {
      show('Choose a make and model above before saving this build.', 'error');
      return;
    }

    const endpoint = root.dataset.etiCommunitySubmissionUrl || '/apps/eti-community';
    const original = control.textContent;
    control.disabled = true;
    control.textContent = 'SAVING...';

    try {
      const response = await fetch(`${endpoint.replace(/\/$/, '')}/commit`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ vehicle, components })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body.ok === false) {
        show(body.error || 'That could not be saved. Try again in a moment.', 'error');
        return;
      }

      const parts = [];
      parts.push(body.createdVehicle ? 'Car added to your garage.' : 'Saved to the car already in your garage.');
      if (body.added) parts.push(`${body.added} ${body.added === 1 ? 'part' : 'parts'} added.`);
      if (body.skipped) parts.push(`${body.skipped} already there.`);
      if (!body.added && !body.skipped) parts.push('Add some parts above and save again to fill it out.');
      show(parts.join(' '), 'ok');

      if (body.garageUrl && result) {
        const link = document.createElement('a');
        link.href = body.garageUrl;
        link.textContent = 'Open ETi Garage';
        result.append(' ', link);
      }
    } catch (error) {
      show('That could not be saved. Check your connection and try again.', 'error');
    } finally {
      control.disabled = false;
      control.textContent = original;
    }
  }

  function handleAction(root, event) {
    const control = event.target.closest('[data-eti-action]');
    if (!control || !root.contains(control)) return;
    const action = control.dataset.etiAction;
    if (action === 'explore-builds') {
      event.preventDefault(); q(root, '[data-eti-community-builds], [data-eti-builds]')?.scrollIntoView({ behavior: motionOK() ? 'smooth' : 'auto', block: 'start' });
    } else if (action === 'commit-build') {
      event.preventDefault(); commitBuild(root, control);
    } else if (action === 'open-submission') {
      event.preventDefault(); openDialog(getDialog(root, 'submission'), control);
    } else if (action === 'open-build') {
      event.preventDefault(); const card = control.closest('[data-eti-build-card]') || q(root, `[data-eti-build-card][data-eti-build-id="${CSS.escape(control.dataset.etiBuildId || '')}"]`); if (card) populateBuildDialog(root, card, control);
    } else if (action === 'close-dialog') {
      event.preventDefault(); const dialog = control.closest('[data-eti-dialog]'); if (dialog?.dataset.etiDialog === 'submission') dialog.__etiCommunityRequestClose?.(); else closeDialog(dialog);
    } else if (action === 'gallery-prev' || action === 'gallery-next') {
      event.preventDefault(); moveGallery(control.closest('[data-eti-dialog]'), action === 'gallery-next' ? 1 : -1);
    } else if (action === 'view-all-builds') {
      event.preventDefault(); qa(root, '[data-eti-community-extra], [data-eti-build-card].is-extra').forEach((card) => card.removeAttribute('hidden')); control.setAttribute('hidden', ''); announce(root, 'All configured builds are shown.'); dispatch(root, 'builds-expanded');
    } else if (action === 'like-build') {
      event.preventDefault(); const card = control.closest('[data-eti-build-card]'); const id = card?.dataset.etiBuildId; if (!id) return; const likes = readStore(STORAGE.likes, {}) || {}; likes[id] = !likes[id]; writeStore(STORAGE.likes, likes); control.classList.toggle('is-liked', likes[id]); control.setAttribute('aria-pressed', String(likes[id])); const count = q(control, '[data-eti-like-count]'); if (count) { if (!count.dataset.etiBaseCount) count.dataset.etiBaseCount = String(Number.parseInt(count.textContent, 10) || 0); count.textContent = String(Number(count.dataset.etiBaseCount) + (likes[id] ? 1 : 0)); } announce(root, likes[id] ? 'Build liked locally.' : 'Local like removed.');
    } else if (action === 'advisor-reset') {
      event.preventDefault(); const form = q(root, '[data-eti-advisor-form]'); form?.reset(); if (root.__etiCommunityGarage) root.__etiCommunityGarage.vehicle = {}; root.__etiCommunityAdvisorUpdate?.(); announce(root, 'Vehicle and build priorities reset. Garage components were kept.');
    } else if (action === 'add-to-build') {
      event.preventDefault(); const id = control.dataset.etiProductId || control.dataset.productId; const product = (root.__etiCommunityProducts || []).find((item) => String(item.id || item.handle) === String(id)); if (!product) return; const added = root.__etiCommunityAddComponent?.(componentFromProduct(product)); announce(root, added ? 'ETi component added to your garage.' : 'That component is already in your garage.');
    } else if (action === 'add-purchase') {
      event.preventDefault(); const card = control.closest('[data-eti-purchase]'); if (!card) return; const component = componentFromProduct({ id: card.dataset.productId, title: card.dataset.title, url: card.dataset.url, image: card.dataset.image, purchaseLabel: card.dataset.purchaseLabel }, 'purchase'); const added = root.__etiCommunityAddComponent?.(component); announce(root, added ? 'Purchased ETi component added to your garage.' : 'That component is already in your garage.');
    } else if (action === 'remove-from-build') {
      event.preventDefault(); const id = control.dataset.etiComponentId || productComponentId(control.dataset.etiProductId); if (!id || !root.__etiCommunityGarage) return; root.__etiCommunityGarage.components = root.__etiCommunityGarage.components.filter((item) => String(item.id) !== String(id)); saveGarage(root); root.__etiCommunityAdvisorUpdate?.(); announce(root, 'Component removed from your garage.');
    } else if (action === 'photo-open') {
      event.preventDefault(); const photo = control.closest('[data-eti-photo]') || control; openLightbox(root, photo, control);
    } else if (action === 'tab') {
      event.preventDefault();
    } else if (action === 'reply-toggle') {
      event.preventDefault(); const card = control.closest('[data-eti-discussion]'); const region = q(card, '[data-eti-reply-region]'); if (!region) return; const open = region.hasAttribute('hidden'); region.toggleAttribute('hidden', !open); control.setAttribute('aria-expanded', String(open)); if (open) q(region, 'textarea, input')?.focus();
    } else if (action === 'like-discussion') {
      event.preventDefault(); const card = control.closest('[data-eti-discussion]'); const id = card?.dataset.etiDiscussionId; if (!id) return; const state = root.__etiCommunityDiscussionState || {}; const liked = !(state[id]?.liked); state[id] = { ...(state[id] || {}), liked }; writeStore(STORAGE.discussions, state); control.classList.toggle('is-liked', liked); control.setAttribute('aria-pressed', String(liked)); const count = q(control, '[data-eti-discussion-like-count]'); if (count) { if (!count.dataset.etiBaseCount) count.dataset.etiBaseCount = count.textContent.trim(); count.textContent = String((Number(count.dataset.etiBaseCount) || 0) + (liked ? 1 : 0)); } announce(root, liked ? 'Discussion liked locally.' : 'Local like removed.');
    } else if (action === 'submission-next') {
      event.preventDefault(); control.closest('form')?.__etiCommunityStep?.next();
    } else if (action === 'submission-back') {
      event.preventDefault(); control.closest('form')?.__etiCommunityStep?.back();
    } else if (action === 'submission-reset') {
      event.preventDefault(); control.closest('form')?.__etiCommunityStep?.reset();
    } else if (action === 'submission-cancel') {
      event.preventDefault(); const dialog = control.closest('[data-eti-dialog="submission"]'); dialog?.__etiCommunityRequestClose?.();
    }
  }

  function setupKeyboard(root) {
    root.addEventListener('keydown', (event) => {
      const dialog = event.target.closest('[data-eti-dialog="build"], [data-eti-dialog="lightbox"]');
      if (!dialog) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); moveGallery(dialog, -1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); moveGallery(dialog, 1); }
    });
  }

  function loadReviewMode(root) {
    const permitted = root.dataset.etiCommunityReviewPermitted === 'true';
    const requested = new URLSearchParams(window.location.search).get('eti_review') === '1';
    if (!permitted || !requested) return;
    const load = () => {
      if (root.dataset.etiCommunityReviewLoaded === 'true') {
        root.dispatchEvent(new CustomEvent(`${NS}:review-toggle`, { bubbles: true })); return;
      }
      const css = root.dataset.etiCommunityReviewCss;
      const script = root.dataset.etiCommunityReviewScript;
      if (!css || !script) return;
      const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = css; link.dataset.etiCommunityReviewAsset = '';
      document.head.append(link);
      const tag = document.createElement('script'); tag.src = script; tag.defer = true; tag.dataset.etiCommunityReviewAsset = '';
      tag.addEventListener('load', () => { root.dataset.etiCommunityReviewLoaded = 'true'; dispatch(root, 'review-ready'); });
      document.head.append(tag);
    };
    root.__etiCommunityLoadReview = load;
    load();
    document.addEventListener('keydown', (event) => {
      if (event.shiftKey && event.altKey && normalized(event.key) === 'r') { event.preventDefault(); load(); }
    });
  }

  function init(root) {
    if (root.dataset.etiCommunityInitialized === 'true') return;
    root.dataset.etiCommunityInitialized = 'true';
    setupDialogs(root);
    setupBuilds(root);
    setupAdvisor(root);
    setupTabs(root);
    setupSwipe(root);
    setupDiscussions(root);
    loadCommunityPhotoFeed(root);
    setupSubmission(root);
    setupKeyboard(root);
    root.addEventListener('click', (event) => handleAction(root, event));
    loadReviewMode(root);
    dispatch(root, 'ready');
  }

  const boot = (scope = document) => qa(scope, '[data-eti-community-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot()); else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
